import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RootState } from '@/app/store';

// Consumer-facing loyalty endpoints (ignite-hub `loyalty/views_public.py`).
// `enabled` gates ALL loyalty UI: a false value means the restaurant has no
// active loyalty program and nothing loyalty-related should render.
export type RewardType = 'percent' | 'fixed' | 'free_item' | 'free_combo' | 'custom';

export interface LoyaltyBalance {
  balance: number;
  enabled: boolean;
  restaurant?: string;
}

export interface LoyaltyReward {
  uuid: string;
  type: RewardType;
  name: string;
  description: string;
  image: string | null;
  value: string | null;
  points_cost: number;
  validity_days: number;
  // Fidélité v2 — true for `fixed`/`percent` rewards with a non-null value:
  // the only ones checkout can apply to an online order.
  usable_online?: boolean;
  // SC-1641 — what the hub would refuse this member now besides points:
  // their own limit is reached, or the stock is gone.
  unavailable?: 'max_uses_reached' | 'out_of_stock' | null;
}

export interface LoyaltyRewardsResponse {
  balance: number;
  enabled: boolean;
  rewards: LoyaltyReward[];
}

export interface RedeemRewardArgs {
  reward_uuid: string;
  restaurant_uuid: string;
}

export interface RedemptionResult {
  code: string;
  reward_type: RewardType;
  reward_name: string;
  value: string | null;
  points_spent: number;
  expires_at: string | null;
  status: string;
  balance: number;
}

// Fidélité v2 (SC-1091) — `GET /loyalty/card/`: member card + coupon wallet in
// one payload. When the restaurant has no active program the server answers
// `{ enabled: false, card_code: null, qr_payload: null, next_reward: null,
// wallet: [] }`; `card_code` is also null until the customer has a relation with
// the restaurant (first order / sign-up), even though the program is on.
export interface LoyaltyNextReward {
  uuid: string;
  name: string;
  points_cost: number;
  missing: number;
}

export interface LoyaltyWalletCoupon {
  code: string;
  qr_payload: string;
  reward_name: string;
  reward_type: RewardType;
  value: string | null;
  points_spent: number;
  issued_at: string;
  expires_at: string | null;
  usable_online: boolean;
}

// Last ledger lines of the customer (hub `history`, added after the first
// release: optional, absent when the program is off or there is no relation).
export interface LoyaltyHistoryEntry {
  kind: 'earn' | 'redeem' | 'adjust';
  /** Signed: a spend is negative, a refund positive. */
  points: number;
  channel: 'online' | 'counter' | 'system';
  /** e.g. `expired_refund`, `counter`, `counter_cancel`; empty for a plain earn/spend. */
  note: string;
  created_at: string;
  reward_name: string | null;
}

export interface LoyaltyCard {
  enabled: boolean;
  balance: number;
  card_code: string | null;
  qr_payload: string | null;
  points_per_euro?: number;
  points_per_visit?: number;
  next_reward: LoyaltyNextReward | null;
  wallet: LoyaltyWalletCoupon[];
  history?: LoyaltyHistoryEntry[];
  // Phone wallets (spec §7.3), all optional: absent until the hub has the
  // credentials. The URLs are SIGNED and expire in 10 minutes — never cache them.
  wallet_passes?: { apple: boolean; google: boolean };
  apple_pass_url?: string;
  google_save_url?: string;
}

// `GET /loyalty/program/?restaurant=` — PUBLIC (no token): what the program pays, so a
// guest can be told what logging in would earn. `{ enabled: false }` exactly when
// the restaurant has no running program. A failed call means « say nothing ».
export type LoyaltyProgram =
  | {
      enabled: true;
      points_per_euro: number;
      points_per_visit: number;
      cheapest_reward: { name: string; points_cost: number } | null;
    }
  | { enabled: false };

// `GET /loyalty/verify/?restaurant=&code=` — what a code typed by hand is worth,
// without spending it. `disabled` = the program is off (hide the coupon UI, it is
// not a bad code). A missing code is a 400 and a throttle a 429 (30/min): both,
// like a network failure, mean « unknown » — the server prices the code at payment.
export type LoyaltyVerifyReason = 'not_found' | 'expired' | 'already_used' | 'disabled';

export type LoyaltyVerifyResult =
  | {
      valid: true;
      code: string;
      reward_name: string;
      reward_type: RewardType;
      value: string | null;
      expires_at: string | null;
      usable_online: boolean;
    }
  | { valid: false; reason: LoyaltyVerifyReason };

export interface LoyaltyVerifyArgs {
  restaurant: string;
  code: string;
}

export const loyaltyApi = createApi({
  reducerPath: 'loyaltyApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_HUB_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).user?.token;
      if (token) headers.set('Authorization', `Token ${token}`);
      return headers;
    },
  }),
  tagTypes: ['LoyaltyBalance', 'LoyaltyRewards', 'LoyaltyCard'],
  endpoints: (build) => ({
    getLoyaltyBalance: build.query<LoyaltyBalance, string>({
      query: (restaurantUuid) => `/loyalty/balance/?restaurant=${restaurantUuid}`,
      providesTags: ['LoyaltyBalance'],
    }),
    getLoyaltyRewards: build.query<LoyaltyRewardsResponse, string>({
      query: (restaurantUuid) => `/loyalty/rewards/?restaurant=${restaurantUuid}`,
      providesTags: ['LoyaltyRewards', 'LoyaltyBalance'],
    }),
    getLoyaltyProgram: build.query<LoyaltyProgram, string>({
      query: (restaurantUuid) => `/loyalty/program/?restaurant=${restaurantUuid}`,
    }),
    getLoyaltyCard: build.query<LoyaltyCard, string>({
      query: (restaurantUuid) => `/loyalty/card/?restaurant=${restaurantUuid}`,
      providesTags: ['LoyaltyCard'],
    }),
    // A mutation on purpose: a GET, but never cached — « already used » must be read
    // fresh every time the customer presses Apply.
    verifyLoyaltyCode: build.mutation<LoyaltyVerifyResult, LoyaltyVerifyArgs>({
      query: ({ restaurant, code }) => ({
        url: `/loyalty/verify/?restaurant=${encodeURIComponent(restaurant)}&code=${encodeURIComponent(code)}`,
        method: 'GET',
      }),
    }),
    redeemReward: build.mutation<RedemptionResult, RedeemRewardArgs>({
      query: (body) => ({
        url: '/loyalty/redeem/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['LoyaltyBalance', 'LoyaltyRewards', 'LoyaltyCard'],
    }),
  }),
});

export const {
  useGetLoyaltyBalanceQuery,
  useGetLoyaltyProgramQuery,
  useGetLoyaltyCardQuery,
  useGetLoyaltyRewardsQuery,
  useRedeemRewardMutation,
  useVerifyLoyaltyCodeMutation,
} = loyaltyApi;
